import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  CurrencyDollar,
  FileText,
  Bell,
  Envelope,
  ChatCircle,
  Info,
  SpinnerGap,
  Warning,
  CheckCircle,
  Lock,
  Lightning,
  ClockCounterClockwise,
  Clock,
} from "phosphor-react";
import { authFetchJson } from "../auth/authApi";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * NotificationPreferences - Contractor Communication Preferences Panel
 * Replaces the old Settings → Notifications tab with high-value notification controls.
 */
export default function NotificationPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entitlements, setEntitlements] = useState({ payment_processing: false });
  const [preferences, setPreferences] = useState({
    // Cash Flow & Payments
    notify_invoice_paid: true,
    notify_partial_payment: true,
    notify_deposit_paid: true,  // P0: Added deposit paid notification
    notify_invoice_overdue_15: true,
    notify_invoice_overdue_30: true,
    notify_invoice_overdue_45: true,
    // Estimates & Sales
    notify_estimate_approved: true,
    notify_estimate_rejected: true,
    notify_customer_viewed_estimate: false,
    notify_customer_viewed_invoice: false,
    // Channels
    contractor_notify_via_in_app: true, // Always ON
    contractor_notify_via_email: false,
    contractor_notify_via_sms: false,
  });
  
  // Follow-up reminder settings (separate from notification preferences)
  const [followupSettings, setFollowupSettings] = useState({
    enabled: false,
    schedule_days: [15, 30, 45],
    send_time_local: "09:00",
    timezone: "America/New_York"
  });
  const [followupLoading, setFollowupLoading] = useState(true);
  const [followupSaving, setFollowupSaving] = useState(false);
  const [scheduleDaysInput, setScheduleDaysInput] = useState("15, 30, 45");

  useEffect(() => {
    fetchPreferences();
    fetchFollowupSettings();
  }, []);

  const fetchPreferences = async () => {
    try {
      const data = await authFetchJson("/tenants/notification-preferences");
      if (data.preferences) {
        setPreferences(data.preferences);
      }
      if (data.entitlements) {
        setEntitlements(data.entitlements);
      }
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
      toast.error("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFollowupSettings = async () => {
    try {
      const data = await authFetchJson("/followups/settings");
      setFollowupSettings(data);
      setScheduleDaysInput(data.schedule_days?.join(", ") || "15, 30, 45");
    } catch (error) {
      console.error("Error fetching follow-up settings:", error);
      // Non-fatal, use defaults
    } finally {
      setFollowupLoading(false);
    }
  };
  
  const updateFollowupSettings = async (updates) => {
    setFollowupSaving(true);
    try {
      const response = await fetch(`${API}/followups/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowupSettings(data);
        setScheduleDaysInput(data.schedule_days?.join(", ") || "15, 30, 45");
        toast.success("Follow-up settings updated");
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update follow-up settings");
      }
    } catch (error) {
      console.error("Error updating follow-up settings:", error);
      toast.error("Failed to update follow-up settings");
    } finally {
      setFollowupSaving(false);
    }
  };
  
  const handleScheduleDaysBlur = () => {
    // Parse comma-separated days
    const days = scheduleDaysInput
      .split(",")
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n) && n >= 1 && n <= 365);
    
    if (days.length === 0) {
      toast.error("Please enter at least one valid day (1-365)");
      setScheduleDaysInput(followupSettings.schedule_days?.join(", ") || "15, 30, 45");
      return;
    }
    
    // Only update if changed
    const currentDays = followupSettings.schedule_days || [];
    const sortedDays = [...new Set(days)].sort((a, b) => a - b);
    if (JSON.stringify(sortedDays) !== JSON.stringify(currentDays)) {
      updateFollowupSettings({ schedule_days: sortedDays });
    }
  };

  const updatePreference = async (key, value) => {
    // Optimistic update
    setPreferences((prev) => ({ ...prev, [key]: value }));
    setSaving(true);

    try {
      const response = await fetch(`${API}/tenants/notification-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          setPreferences(data.preferences);
        }
        toast.success("Preference updated");
      } else {
        // Revert on error
        setPreferences((prev) => ({ ...prev, [key]: !value }));
        toast.error("Failed to update preference");
      }
    } catch (error) {
      // Revert on error
      setPreferences((prev) => ({ ...prev, [key]: !value }));
      toast.error("Failed to update preference");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-zinc-200">
        <CardContent className="py-12 flex items-center justify-center">
          <SpinnerGap size={32} className="animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-preferences">
      {/* Header Card */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Bell size={20} className="text-[#FF6600]" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Control which notifications you receive about your business activity.
            These settings affect your in-app bell notifications.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Cash Flow & Payments Section */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CurrencyDollar size={18} className="text-emerald-600" />
            Cash Flow & Payments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Payment Processing Entitlement Gate for Invoice Paid & Partial Payment */}
          {!entitlements.payment_processing && (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Lightning size={20} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-900">Enable Payment Notifications</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Get notified instantly when customers pay invoices. Subscribe to Payment Processing to unlock these powerful cash flow alerts.
                  </p>
                  <Link to="/billing">
                    <Button 
                      size="sm" 
                      className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="upgrade-payment-processing-btn"
                    >
                      <Lightning size={14} className="mr-2" />
                      Enable Payment Processing
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
          
          <PreferenceToggle
            label="Invoice Paid"
            description="Get notified when a customer pays an invoice in full"
            checked={preferences.notify_invoice_paid}
            onChange={(val) => updatePreference("notify_invoice_paid", val)}
            testId="pref-invoice-paid"
            disabled={!entitlements.payment_processing}
            lockedMessage={!entitlements.payment_processing ? "Requires Payment Processing" : null}
          />
          <PreferenceToggle
            label="Partial Payment Received"
            description="Get notified when a customer makes a partial payment"
            checked={preferences.notify_partial_payment}
            onChange={(val) => updatePreference("notify_partial_payment", val)}
            testId="pref-partial-payment"
            disabled={!entitlements.payment_processing}
            lockedMessage={!entitlements.payment_processing ? "Requires Payment Processing" : null}
          />
          <PreferenceToggle
            label="Deposit Paid"
            description="Get notified when a customer pays an estimate deposit"
            checked={preferences.notify_deposit_paid}
            onChange={(val) => updatePreference("notify_deposit_paid", val)}
            testId="pref-deposit-paid"
            disabled={!entitlements.payment_processing}
            lockedMessage={!entitlements.payment_processing ? "Requires Payment Processing" : null}
            badge="NEW"
            badgeColor="emerald"
          />
          
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm text-zinc-500 uppercase tracking-wide mb-3 block">
              Overdue Invoice Alerts
            </Label>
            <div className="space-y-4">
              <PreferenceToggle
                label="Invoice Overdue — 15 Days"
                description="Alert when an invoice becomes 15 days past due"
                checked={preferences.notify_invoice_overdue_15}
                onChange={(val) => updatePreference("notify_invoice_overdue_15", val)}
                testId="pref-overdue-15"
                badge="NEW"
                badgeColor="emerald"
              />
              <PreferenceToggle
                label="Invoice Overdue — 30 Days"
                description="Alert when an invoice becomes 30 days past due"
                checked={preferences.notify_invoice_overdue_30}
                onChange={(val) => updatePreference("notify_invoice_overdue_30", val)}
                testId="pref-overdue-30"
              />
              <PreferenceToggle
                label="Invoice Overdue — 45 Days"
                description="Alert when an invoice becomes 45 days past due"
                checked={preferences.notify_invoice_overdue_45}
                onChange={(val) => updatePreference("notify_invoice_overdue_45", val)}
                testId="pref-overdue-45"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estimates & Sales Section */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Estimates & Sales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <PreferenceToggle
            label="Estimate Approved"
            description="Get notified when a customer approves your estimate"
            checked={preferences.notify_estimate_approved}
            onChange={(val) => updatePreference("notify_estimate_approved", val)}
            testId="pref-estimate-approved"
          />
          <PreferenceToggle
            label="Estimate Rejected"
            description="Get notified when a customer rejects your estimate"
            checked={preferences.notify_estimate_rejected}
            onChange={(val) => updatePreference("notify_estimate_rejected", val)}
            testId="pref-estimate-rejected"
          />
          
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm text-zinc-500 uppercase tracking-wide mb-3 block flex items-center gap-2">
              Sales Intelligence
              <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-300">
                Optional
              </Badge>
            </Label>
            <div className="space-y-4">
              <PreferenceToggle
                label="Customer Viewed Estimate"
                description="Get notified when a customer opens your estimate link"
                checked={preferences.notify_customer_viewed_estimate}
                onChange={(val) => updatePreference("notify_customer_viewed_estimate", val)}
                testId="pref-viewed-estimate"
                defaultOff
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Follow-Up Reminders Section (Customer-Facing) */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-orange-600" />
            Invoice Follow-Up Reminders
            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300 bg-orange-50">
              Customer-Facing
            </Badge>
          </CardTitle>
          <CardDescription className="text-sm mt-1">
            Automatically send payment reminder emails to customers with overdue invoices.
            These are sent directly to customers, not to you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {followupLoading ? (
            <div className="flex items-center justify-center py-4">
              <SpinnerGap size={24} className="animate-spin text-zinc-400" />
            </div>
          ) : (
            <>
              {/* Enable Toggle */}
              <div className="flex items-center justify-between py-3 px-4 bg-zinc-50 rounded-lg border border-zinc-200">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium">Enable Automatic Follow-Ups</Label>
                  </div>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    When enabled, reminder emails are sent automatically on your schedule
                  </p>
                </div>
                <Switch
                  checked={followupSettings.enabled}
                  onCheckedChange={(val) => updateFollowupSettings({ enabled: val })}
                  data-testid="followup-enabled-toggle"
                  disabled={followupSaving}
                />
              </div>
              
              {/* Settings Panel (shown when enabled) */}
              {followupSettings.enabled && (
                <div className="space-y-4 p-4 bg-orange-50/50 border border-orange-200 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Reminder Schedule (days after due date)</Label>
                    <Input
                      value={scheduleDaysInput}
                      onChange={(e) => setScheduleDaysInput(e.target.value)}
                      onBlur={handleScheduleDaysBlur}
                      placeholder="15, 30, 45"
                      className="mt-1.5 h-10 border-2"
                      data-testid="followup-schedule-days"
                      disabled={followupSaving}
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Enter days separated by commas (e.g., 15, 30, 45)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Send Time</Label>
                    <Input
                      type="time"
                      value={followupSettings.send_time_local || "09:00"}
                      onChange={(e) => updateFollowupSettings({ send_time_local: e.target.value })}
                      className="mt-1.5 h-10 border-2 w-32"
                      data-testid="followup-send-time"
                      disabled={followupSaving}
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Reminders sent at this time in your timezone ({followupSettings.timezone})
                    </p>
                  </div>
                  
                  {/* Preview */}
                  <div className="pt-3 border-t border-orange-200">
                    <Label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">
                      Current Schedule
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {followupSettings.schedule_days?.map((day) => (
                        <Badge
                          key={day}
                          variant="outline"
                          className="text-sm text-orange-700 border-orange-300 bg-white"
                        >
                          <Clock size={12} className="mr-1" />
                          {day} days overdue
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info Box */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Follow-up reminders are automatically canceled when an invoice is marked as paid.
                  Emails are sent to the customer associated with each overdue invoice.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notification Channels Section */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ChatCircle size={18} className="text-purple-600" />
            Notification Channels
          </CardTitle>
          <CardDescription className="text-sm">
            Choose how you want to receive notifications. In-app notifications are always enabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* In-App - Always ON */}
          <div className="flex items-center justify-between py-3 px-4 bg-zinc-50 rounded-lg border border-zinc-200">
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-zinc-600" />
              <div>
                <Label className="font-medium">In-App Notifications</Label>
                <p className="text-sm text-zinc-500">Bell icon notifications in the dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <CheckCircle size={12} className="mr-1" />
                Always On
              </Badge>
            </div>
          </div>

          {/* Email Notifications */}
          <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-zinc-200">
            <div className="flex items-center gap-3">
              <Envelope size={20} className="text-zinc-600" />
              <div>
                <Label className="font-medium">Email Notifications</Label>
                <p className="text-sm text-zinc-500">Receive notifications via email</p>
              </div>
            </div>
            <Switch
              checked={preferences.contractor_notify_via_email}
              onCheckedChange={(val) => updatePreference("contractor_notify_via_email", val)}
              data-testid="pref-channel-email"
            />
          </div>

          {/* SMS Notifications */}
          <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-zinc-200">
            <div className="flex items-center gap-3">
              <ChatCircle size={20} className="text-zinc-600" />
              <div>
                <Label className="font-medium">SMS Notifications</Label>
                <p className="text-sm text-zinc-500">Receive notifications via text message</p>
              </div>
            </div>
            <Switch
              checked={preferences.contractor_notify_via_sms}
              onCheckedChange={(val) => updatePreference("contractor_notify_via_sms", val)}
              data-testid="pref-channel-sms"
            />
          </div>

          {/* Info Box */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4">
            <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">Cost Notice</p>
              <p className="text-sm text-amber-700 mt-1">
                Email and SMS notifications may incur additional costs depending on your plan.
                In-app notifications are always free and cannot be disabled.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">About these notifications</p>
          <p className="text-sm text-blue-700 mt-1">
            These settings control notifications about your business activity. Customer-facing
            communications (like invoice reminders sent to customers) are handled separately and
            will continue to work regardless of these settings.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * PreferenceToggle - Reusable toggle row component
 */
function PreferenceToggle({ 
  label, 
  description, 
  checked, 
  onChange, 
  testId,
  badge,
  badgeColor = "amber",
  defaultOff = false,
  disabled = false,
  lockedMessage = null
}) {
  const badgeColors = {
    amber: "text-amber-600 border-amber-300 bg-amber-50",
    emerald: "text-emerald-600 border-emerald-300 bg-emerald-50",
    blue: "text-blue-600 border-blue-300 bg-blue-50",
  };

  return (
    <div className={`flex items-center justify-between ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex-1 pr-4">
        <div className="flex items-center gap-2">
          <Label className={`font-medium ${disabled ? 'text-zinc-500' : ''}`}>{label}</Label>
          {badge && (
            <Badge variant="outline" className={`text-xs ${badgeColors[badgeColor]}`}>
              {badge}
            </Badge>
          )}
          {defaultOff && (
            <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-300">
              Off by default
            </Badge>
          )}
          {lockedMessage && (
            <Badge variant="outline" className="text-xs text-zinc-400 border-zinc-300 bg-zinc-50">
              <Lock size={10} className="mr-1" />
              {lockedMessage}
            </Badge>
          )}
        </div>
        <p className="text-sm text-zinc-500 mt-0.5">{description}</p>
      </div>
      <Switch
        checked={disabled ? false : checked}
        onCheckedChange={onChange}
        data-testid={testId}
        disabled={disabled}
      />
    </div>
  );
}
